import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Button, Spinner, Image, Card, Form } from 'react-bootstrap';
import axios from 'axios'; // Adicionado para consistência
import { useNavigate } from 'react-router-dom';
import { useEntidadeContext } from '../EntidadeContext';
import { useAuthContext } from '../context/AuthContext';
import CommunicationModal from '../components/consultorio/CommunicationModal';
import logoImage from '../img/logo.png';
import Select from 'react-select'; // 1. Importar o react-select
import { useAlertModal } from '../AlertModalContext';

const ComunicadosPage = () => {
  const { currentEntidade, isLoadingEntidade } = useEntidadeContext();
  const { loggedInUser: currentUser } = useAuthContext();
  const { showAlertModal } = useAlertModal(); // Importar para o alerta
  const navigate = useNavigate(); // Importar para o redirecionamento

  const [pacientesList, setPacientesList] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [allTags, setAllTags] = useState([]); // 2. Estado para todas as tags
  const [selectedTags, setSelectedTags] = useState([]); // 3. Estado para as tags selecionadas
  const [dynamicFilter, setDynamicFilter] = useState(''); // Novo estado para filtros dinâmicos

  useEffect(() => {
    const fetchData = async (url) => {
      if (!currentEntidade) return;

      setIsLoadingData(true);
      try {
        // Busca pacientes e tags em paralelo para otimizar
        const [pacientesResponse, tagsResponse] = await Promise.all([
          axios.get(url), // URL agora é um parâmetro
          axios.get(`/api/interesses`) // A API já filtra pela entidade do usuário logado
        ]);

        setPacientesList(pacientesResponse.data.pacientes || []);

        // Só atualiza as tags se elas ainda não foram carregadas, para evitar re-renderizações desnecessárias do Select
        if (allTags.length === 0) {
          const formattedTags = (tagsResponse.data.interesses || []).map(tag => ({
            value: tag.id,
            label: tag.nome
          }));
          setAllTags(formattedTags);
        }

      } catch (error) {
        console.error("Erro ao buscar a lista de pacientes:", error);
        setPacientesList([]);
        setAllTags([]);
      } finally {
        setIsLoadingData(false);
      }
    };

    // Define qual URL usar com base no filtro dinâmico
    let fetchUrl;
    if (dynamicFilter === 'pacientes_inativos') {
      fetchUrl = `/api/entidades/${currentEntidade.id}/pacientes/inativos`;
    } else {
      // URL padrão para buscar todos os pacientes com seus dados completos
      fetchUrl = `/api/entidades/${currentEntidade.id}/pacientes`;
    }

    fetchData(fetchUrl);

  }, [currentEntidade, dynamicFilter]); // Re-executa quando a entidade ou o filtro dinâmico muda

  // Memo para filtrar a lista de pacientes com base nos filtros
  const filteredPacientesList = useMemo(() => {
    let tempList = [...pacientesList];

    // 1. Aplica o filtro dinâmico primeiro
    if (dynamicFilter === 'aniversariantes_mes') {
      const currentMonth = new Date().getMonth() + 1; // getMonth() é 0-indexado (0-11)
      tempList = tempList.filter(paciente => {
        if (!paciente.data_nascimento) return false;
        // A API já retorna uma string ISO completa. new Date() a interpreta corretamente.
        const birthMonth = new Date(paciente.data_nascimento).getMonth() + 1;
        return birthMonth === currentMonth;
      });
    } else if (dynamicFilter === 'aniversariantes_30_dias') {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Zera a hora para normalizar a data

      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);

      tempList = tempList.filter(paciente => {
        if (!paciente.data_nascimento) return false;

        const birthDate = new Date(paciente.data_nascimento); // Apenas new Date()
        const birthMonth = birthDate.getMonth();
        const birthDay = birthDate.getDate();

        // Verifica o aniversário para este ano
        const birthdayThisYear = new Date(today.getFullYear(), birthMonth, birthDay);
        if (birthdayThisYear >= today && birthdayThisYear <= thirtyDaysFromNow) {
          return true;
        }

        // Se o aniversário deste ano já passou, verifica para o próximo ano
        const birthdayNextYear = new Date(today.getFullYear() + 1, birthMonth, birthDay);
        return birthdayNextYear >= today && birthdayNextYear <= thirtyDaysFromNow;
      });
    } else if (dynamicFilter === 'aniversariantes_dia') {
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentDay = today.getDate();
      tempList = tempList.filter(paciente => {
        if (!paciente.data_nascimento) return false;
        const birthDate = new Date(paciente.data_nascimento);
        const birthMonth = birthDate.getMonth() + 1;
        const birthDay = birthDate.getDate();
        return birthMonth === currentMonth && birthDay === currentDay;
      });
    }
    // A lógica para 'pacientes_inativos' foi movida para o backend e é controlada pelo useEffect.

    // 2. Depois, aplica o filtro de tags no resultado do filtro dinâmico
    if (selectedTags.length > 0) {
      const selectedTagIds = new Set(selectedTags.map(tag => tag.value));
      tempList = tempList.filter(paciente =>
        paciente.interesses && paciente.interesses.some(interesse => selectedTagIds.has(interesse.id))
      );
    }
    return tempList;
  }, [pacientesList, selectedTags, dynamicFilter]);
  
  // Função para abrir o modal de comunicação, com validação prévia
  const handleOpenCommunicationModal = () => {
    if (currentEntidade && currentEntidade.email_config_complete) {
      setShowModal(true);
    } else {
      showAlertModal(
        'Configuração de E-mail Pendente',
        'As configurações de envio de e-mail (SMTP) não foram realizadas. Isso é necessário para enviar comunicados. Clique em OK para configurar agora.',
        'warning',
        () => navigate('/configuracoes') // Redireciona para a página de configurações
      );
    }
  };


  if (isLoadingEntidade || isLoadingData) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <>
      <div style={{ border: '1px solid gray', backgroundColor: '#f5f5f5' }}>
        <div className="header" style={{ minHeight: '80px' }}>
          <Row className="p-1 align-items-center" style={{ minHeight: 'inherit' }}>
            <Col xs={12} md={6} style={{ minHeight: '60px' }}>
              <div className="d-flex align-items-center justify-content-start h-100">
                <Image src={logoImage} alt="Logo Clínica" style={{ maxHeight: '60px', marginLeft: '12px' }} />
                <h3 style={{ marginLeft: '15px', marginBottom: '0' }}>Envio de Comunicados</h3>
              </div>
            </Col>
          </Row>
        </div>
        <div className="body p-3">
          <Card>
            <Card.Body>
              <Card.Title>Comunicação em Massa</Card.Title>
              <Form.Group className="my-3">
                <Form.Label>Filtros Dinâmicos:</Form.Label>
                <div>
                  <Button
                    variant={dynamicFilter === 'aniversariantes_mes' ? 'primary' : 'outline-primary'}
                    onClick={() => setDynamicFilter(prev => prev === 'aniversariantes_mes' ? '' : 'aniversariantes_mes')}
                    className="me-2"
                  >
                    Aniversariantes do Mês
                  </Button>
                  <Button
                    variant={dynamicFilter === 'pacientes_inativos' ? 'primary' : 'outline-primary'}
                    onClick={() => setDynamicFilter(prev => prev === 'pacientes_inativos' ? '' : 'pacientes_inativos')}
                    className="me-2"
                  >
                    Pacientes Inativos (6 meses)
                  </Button>
                  <Button
                    variant={dynamicFilter === 'aniversariantes_dia' ? 'primary' : 'outline-primary'}
                    onClick={() => setDynamicFilter(prev => prev === 'aniversariantes_dia' ? '' : 'aniversariantes_dia')}
                    className="me-2"
                  >
                    Aniversariantes do Dia
                  </Button>
                  {/* Outros botões de filtros dinâmicos podem ser adicionados aqui no futuro */}
                </div>
              </Form.Group>
              <Form.Group controlId="filtroInteresses" className="my-3">
                <Form.Label>Filtrar pacientes por interesse:</Form.Label>
                <Select
                  isMulti
                  options={allTags}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Selecione um ou mais interesses..."
                  noOptionsMessage={() => 'Nenhuma tag encontrada'}
                  value={selectedTags}
                  onChange={setSelectedTags}
                />
              </Form.Group>
              <Card.Text>
                {selectedTags.length > 0 || dynamicFilter
                  ? `Será enviado um comunicado para ${filteredPacientesList.length} paciente(s) que correspondem aos filtros selecionados.`
                  : `Utilize esta ferramenta para enviar comunicados, promoções ou avisos gerais para todos os ${pacientesList.length} pacientes cadastrados.`
                }
              </Card.Text>
              <Button variant="primary" onClick={handleOpenCommunicationModal}>
                Criar Novo Comunicado
              </Button>
            </Card.Body>
          </Card>
        </div>
      </div>

      <CommunicationModal
        show={showModal}
        onHide={() => setShowModal(false)}
        mode="comunicado" // Novo modo de operação
        appointments={[]} // Não aplicável neste modo, mas mantido para a prop do modal
        pacientesList={filteredPacientesList} // 5. Passa a lista JÁ FILTRADA para o modal
        todosMedicos={[]} // Não aplicável
        especialidades={[]} // Não aplicável
        currentEntidade={currentEntidade}
        currentUser={currentUser}
        onSendComplete={() => {
          // Ação opcional após o envio, como um alerta de sucesso.
          // A lógica de alerta já está dentro do modal.
        }}
      />
    </>
  );
};

export default ComunicadosPage;